import React, { useRef, useState } from "react";
import {
  Box,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Radio,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { countries } from "../PaymentPages/countryData";
import { useOfferCheckoutStyles } from "./offerCheckout.styles";

// Select clones every child with option props (onClick, role, value…); the
// search row must drop them or clicking it would pick an empty value.
// eslint-disable-next-line no-unused-vars
const SearchRow = ({ onClick, onKeyUp, role, selected, value, ...props }) => (
  <ListSubheader {...props} />
);

// Escape and Tab still reach the menu (close); everything else stays in the
// input so the menu's type-ahead can't steal focus. ArrowDown hands focus to
// the first result — Select's disableListWrap blocks MenuList from doing it.
const onSearchKeyDown = (e) => {
  if (e.key === "Escape" || e.key === "Tab") return;
  e.stopPropagation();
  if (e.key === "ArrowDown") {
    e.preventDefault();
    const first = e.currentTarget
      .closest("ul")
      ?.querySelector('[role="option"]:not([aria-disabled="true"])');
    if (first) first.focus();
  }
};

const CountryCodeSelect = ({ country, onCountryChange, classes }) => {
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const q = query.trim().toLowerCase();
  const shown = q
    ? countries.filter((c) =>
        `${c.name || ""} ${c.dial_code}`.toLowerCase().includes(q)
      )
    : countries;

  return (
    <Select
      value={country?.code || "US"}
      onChange={(e) => {
        const next = countries.find((c) => c.code === e.target.value);
        if (next && onCountryChange) onCountryChange(next);
      }}
      onClose={() => setQuery("")}
      disableUnderline
      MenuProps={{
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        transformOrigin: { vertical: "top", horizontal: "left" },
        getContentAnchorEl: null,
        disableAutoFocusItem: true,
        TransitionProps: {
          onEntered: () => searchRef.current && searchRef.current.focus(),
        },
        PaperProps: { style: { maxHeight: 320, minWidth: 260 } },
      }}
      renderValue={() => (
        <Box className={classes.phonePrefix}>
          {country?.flag} {country?.dial_code}
        </Box>
      )}
    >
      <SearchRow className={classes.countrySearchRow}>
        <TextField
          inputRef={searchRef}
          fullWidth
          size="small"
          placeholder="Search country..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" style={{ color: "#9aa19d" }} />
              </InputAdornment>
            ),
          }}
        />
      </SearchRow>
      {shown.map((c) => (
        <MenuItem key={c.code} value={c.code}>
          {c.flag} {c.name} ({c.dial_code})
        </MenuItem>
      ))}
      {shown.length === 0 && (
        <MenuItem disabled>No countries found</MenuItem>
      )}
    </Select>
  );
};

// Digit cap per phone field. Must equal the validator's rule, or a value could
// pass validation that the input won't let anyone type (or vice versa).
const phoneMaxLength = (field) =>
  field.phoneRule === "ae_mobile" ? 10 : field.international ? 14 : 10;

/**
 * Autofill token per field key. Every text field used to claim "name", which
 * told iOS that Company Name was a person's name — the keyboard then offered
 * the buyer's own name and leads arrived with Company === Name. A wrong token
 * is worse than none, so anything unmapped opts out.
 */
const AUTOCOMPLETE_BY_KEY = {
  name: "name",
  fullName: "name",
  organizationName: "organization",
  email: "email",
};

const autoCompleteFor = (field) =>
  field.autoComplete || AUTOCOMPLETE_BY_KEY[field.key] || "off";

/**
 * Renders a single form field based on its config.
 * Closed enum of supported types: text | email | phone | select | radio |
 * textarea. Unknown types render nothing + console.warn (fail-soft — a
 * misconfigured field never breaks the page).
 */
const FormFieldRenderer = ({
  field,
  value,
  error,
  onChange,
  country,
  onCountryChange,
}) => {
  const classes = useOfferCheckoutStyles();

  const handle = (ev) => {
    let v = ev.target.value;
    if (field.type === "phone") {
      // Digits only. The cap must match the input's maxLength — an
      // international number is up to 14 digits, so the India-only 10 would
      // silently truncate it as the user types.
      v = v.replace(/\D/g, "").slice(0, phoneMaxLength(field));
    }
    onChange(field.key, v);
  };

  const label = `${field.label}${field.required ? "*" : ""}`;

  const common = {
    fullWidth: true,
    variant: "outlined",
    required: Boolean(field.required),
    name: field.key,
    placeholder: field.placeholder || "",
    value: value ?? "",
    onChange: handle,
    className: classes.formField,
    error: Boolean(error),
    helperText: error || field.helpText || undefined,
    InputLabelProps: { shrink: false },
  };

  switch (field.type) {
    case "text":
      return (
        <>
          <Typography variant="body2" className={classes.formFieldLabel}>
            {label}
          </Typography>
          <TextField {...common} autoComplete={autoCompleteFor(field)} />
        </>
      );

    case "email":
      return (
        <>
          <Typography variant="body2" className={classes.formFieldLabel}>
            {label}
          </Typography>
          <TextField {...common} type="email" autoComplete="email" />
        </>
      );

    case "phone": {
      // International pages get a country selector; India-only pages keep the
      // fixed +91 prefix and 10-digit cap they ship with today.
      const international = Boolean(field.international);
      // phoneRule "ae_mobile" locks the country to UAE: a fixed +971 prefix
      // instead of the selector (the page's defaultCountry seeds AE state).
      const lockedAe = field.phoneRule === "ae_mobile";
      return (
        <>
          <Typography variant="body2" className={classes.formFieldLabel}>
            {label}
          </Typography>
          <TextField
            {...common}
            type="tel"
            autoComplete={international ? "tel" : "tel-national"}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              // 14 digits covers international national numbers; capping at
              // 10 (the India rule) would silently truncate valid ones.
              maxLength: phoneMaxLength(field),
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {lockedAe ? (
                    <Box className={classes.phonePrefix}>🇦🇪 +971</Box>
                  ) : international ? (
                    <CountryCodeSelect
                      country={country}
                      onCountryChange={onCountryChange}
                      classes={classes}
                    />
                  ) : (
                    <Box className={classes.phonePrefix}>+91</Box>
                  )}
                </InputAdornment>
              ),
            }}
          />
        </>
      );
    }

    case "textarea":
      return (
        <>
          <Typography variant="body2" className={classes.formFieldLabel}>
            {label}
          </Typography>
          <TextField {...common} multiline minRows={3} />
        </>
      );

    case "select":
      return (
        <>
          <Typography variant="body2" className={classes.formFieldLabel}>
            {label}
          </Typography>
          <TextField {...common} select>
            {(field.options || []).map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </>
      );

    case "radio":
      // Same control as the ind-plans-1 checkout: a visible radio in a
      // bordered row, two per line and one per line on a phone. Tap-to-choose
      // beats a dropdown here — a native select opens a modal wheel, which is
      // two extra taps immediately before Pay Now.
      return (
        <>
          <Typography variant="body2" className={classes.formFieldLabel}>
            {label}
          </Typography>
          <Box className={classes.radioGroup}>
            {(field.options || []).map((opt) => {
              const selected = value === opt.value;
              return (
                <Box key={opt.value} className={classes.radioButton}>
                  <label
                    className={`${classes.radioLabel} ${
                      selected ? classes.radioLabelSelected : ""
                    }`}
                  >
                    <Radio
                      name={field.key}
                      value={opt.value}
                      checked={selected}
                      onChange={() => onChange(field.key, opt.value)}
                      className={classes.radioRoot}
                      color="primary"
                      size="small"
                    />
                    <Typography variant="body2">{opt.label}</Typography>
                  </label>
                </Box>
              );
            })}
          </Box>
          {error ? (
            <Typography className={classes.radioErrorText}>{error}</Typography>
          ) : null}
        </>
      );

    default:
      // eslint-disable-next-line no-console
      console.warn(
        `[FormFieldRenderer] Unknown field type "${field.type}" for key "${field.key}" — skipping.`
      );
      return null;
  }
};

export default FormFieldRenderer;
